// pages/components/support/CreateTicket.jsx
import React, { useState } from 'react';
import { Send, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import apiRequest from '@/services/requests';

const CreateTicket = ({ onSuccess }) => {
const [formData, setFormData] = useState({
subject: '',
priority: 'medium',
message: ''
});
const [submitting, setSubmitting] = useState(false);
const [result, setResult] = useState(null);

const priorities = [
{ value: 'low', label: 'Low', color: 'slate', description: 'General inquiry' },
{ value: 'medium', label: 'Medium', color: 'blue', description: 'Standard issue' },
{ value: 'high', label: 'High', color: 'amber', description: 'Important matter' },
{ value: 'urgent', label: 'Urgent', color: 'red', description: 'Critical issue' }
];

const handleInputChange = (e) => {
const { name, value } = e.target;
setFormData(prev => ({ ...prev, [name]: value }));
setResult(null);
};

const handleSubmit = async (e) => {
e.preventDefault();

if (!formData.subject.trim() || !formData.message.trim()) {
setResult({
type: 'error',
message: 'Please fill in all required fields'
});
return;
}

setSubmitting(true);
setResult(null);

try {
const res = await apiRequest.createSupportTicket(formData);

if (res.success) {
setResult({
type: 'success',
message: res.data.message,
ticketId: res.data.ticketId
});

// Reset form
setFormData({
subject: '',
priority: 'medium',
message: ''
});

// Call success callback after a delay
setTimeout(() => {
if (onSuccess) onSuccess();
}, 2000);
}
} catch (error) {
setResult({
type: 'error',
message: error.response?.data?.error?.message || 'Failed to create ticket. Please try again.'
});
}

setSubmitting(false);
};

const selectedPriority = priorities.find(p => p.value === formData.priority);

return (
<div className="max-w-3xl mx-auto">
<div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
<div className="mb-8">
<h2 className="text-2xl font-bold text-slate-900 mb-2">Create Support Ticket</h2>
<p className="text-slate-600">
Describe your issue and our team will get back to you within 24 hours
</p>
</div>

<form onSubmit={handleSubmit} className="space-y-6">
{/* Subject */}
<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Subject *
</label>
<input
type="text"
name="subject"
value={formData.subject}
onChange={handleInputChange}
placeholder="Brief description of your issue"
required
className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
/>
</div>

{/* Priority */}
<div>
<label className="block text-sm font-semibold text-slate-700 mb-3">
Priority *
</label>
<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
{priorities.map(priority => {
const isSelected = formData.priority === priority.value;
const colorClasses = {
slate: 'border-slate-300 bg-slate-50 text-slate-700',
blue: 'border-blue-300 bg-blue-50 text-blue-700',
amber: 'border-amber-300 bg-amber-50 text-amber-700',
red: 'border-red-300 bg-red-50 text-red-700'
};
const selectedClasses = {
slate: 'border-slate-600 bg-slate-100 ring-2 ring-slate-400',
blue: 'border-blue-600 bg-blue-100 ring-2 ring-blue-400',
amber: 'border-amber-600 bg-amber-100 ring-2 ring-amber-400',
red: 'border-red-600 bg-red-100 ring-2 ring-red-400'
};

return (
<button
key={priority.value}
type="button"
onClick={() => setFormData(prev => ({ ...prev, priority: priority.value }))}
className={`
  px-4 py-3 rounded-lg border-2 font-medium transition-all text-center
  ${isSelected ? selectedClasses[priority.color] : colorClasses[priority.color]}
  hover:shadow-md
`}
>
<p className="font-bold">{priority.label}</p>
<p className="text-xs mt-1">{priority.description}</p>
</button>
);
})}
</div>
</div>

{/* Message */}
<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Message *
</label>
<textarea
name="message"
value={formData.message}
onChange={handleInputChange}
placeholder="Provide detailed information about your issue..."
required
rows={8}
className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
/>
<p className="text-xs text-slate-500 mt-2">
{formData.message.length} / 2000 characters
</p>
</div>

{/* Result Message */}
{result && (
<div className={`p-4 rounded-lg border-2 ${
result.type === 'success' 
? 'bg-green-50 border-green-200' 
: 'bg-red-50 border-red-200'
}`}>
<div className="flex items-start gap-3">
{result.type === 'success' ? (
<CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
) : (
<AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
)}
<div className="flex-1">
<p className={`font-semibold ${
result.type === 'success' ? 'text-green-900' : 'text-red-900'
}`}>
{result.type === 'success' ? 'Success!' : 'Error'}
</p>
<p className={`text-sm mt-1 ${
result.type === 'success' ? 'text-green-700' : 'text-red-700'
}`}>
{result.message}
</p>
{result.ticketId && (
<p className="text-sm font-mono bg-white px-3 py-2 rounded mt-2 text-green-800">
  Ticket ID: {result.ticketId}
</p>
)}
</div>
</div>
</div>
)}

{/* Submit Button */}
<div className="flex gap-4 pt-4">
<button
type="submit"
disabled={submitting}
className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 disabled:from-blue-400 disabled:to-cyan-400 text-white font-semibold rounded-lg shadow-md transition-all"
>
{submitting ? (
<>
<Loader2 className="animate-spin" size={20} />
Creating Ticket...
</>
) : (
<>
<Send size={20} />
Submit Ticket
</>
)}
</button>
</div>
</form>

{/* Info Box */}
<div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
<h4 className="font-semibold text-blue-900 mb-2">💡 Tips for faster support</h4>
<ul className="text-sm text-blue-800 space-y-1">
<li className="flex items-start gap-2">
<span className="text-blue-600 mt-0.5">•</span>
<span>Be specific about the issue you're experiencing</span>
</li>
<li className="flex items-start gap-2">
<span className="text-blue-600 mt-0.5">•</span>
<span>Include relevant details like error messages or screenshots</span>
</li>
<li className="flex items-start gap-2">
<span className="text-blue-600 mt-0.5">•</span>
<span>Choose the appropriate priority level</span>
</li>
</ul>
</div>
</div>
</div>
);
};

export default CreateTicket;